import React, { useMemo, useState, useEffect, useRef, useId } from "react";
import * as ChartJS from "chart.js";
import MdOutlineAssignment from "../../constants/icons/Md/MdOutlineAssignment";
import BsClock from "../../constants/icons/Bs/BsClock";

ChartJS.Chart.register(
  ChartJS.BarElement,
  ChartJS.CategoryScale,
  ChartJS.LinearScale,
  ChartJS.Tooltip,
  ChartJS.Legend
);

/* ------------------------------------------------------------------ */
/*  Palette / type — system fonts only, no external font loading      */
/* ------------------------------------------------------------------ */
const INK = "#1C2541";
const PAPER = "#F7F6F2";
const CARD = "#FFFFFF";
const HAIRLINE = "#E4E1D8";
const GOLD = "#B8862F";

const DISPLAY_FONT = "Georgia, 'Iowan Old Style', 'Times New Roman', serif";
const BODY_FONT =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

const BAND_COLORS = {
  low: "#C1573F", // < 4  — needs work
  mid: "#D6A23C", // 4–5.5 — developing
  good: "#3E7C99", // 6–6.5 — competent
  high: "#2F7A56", // 7+   — proficient
  empty: "#D7D7D2", // no graded data
};

function bandColor(avg) {
  if (avg == null) return BAND_COLORS.empty;
  if (avg < 4) return BAND_COLORS.low;
  if (avg < 6) return BAND_COLORS.mid;
  if (avg < 7) return BAND_COLORS.good;
  return BAND_COLORS.high;
}

function hexToRgba(hex, alpha) {
  const h = hex.replace("#", "");
  const bigint = parseInt(h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/* ------------------------------------------------------------------ */
/*  Score helpers                                                     */
/* ------------------------------------------------------------------ */

// Rounds UP to the nearest 0.5 (4.2 -> 4.5, 4.7 -> 5.0), per spec.
function ceilToHalf(n) {
  if (!isFinite(n)) return 0;
  return Math.ceil(n * 2) / 2;
}

function isPending(item) {
  return (
    item.score === undefined ||
    item.score === null ||
    item.gradingStatus !== "graded"
  );
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/* ------------------------------------------------------------------ */
/*  Bucketing — the x-axis shape changes per filter                   */
/* ------------------------------------------------------------------ */
const HOUR_LABELS = Array.from({ length: 24 }, (_, h) => {
  const period = h < 12 ? "AM" : "PM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}${period}`;
});
const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function buildBuckets(filter, now) {
  if (filter === "today") {
    return Array.from({ length: 24 }, (_, h) => ({
      key: `h${h}`,
      label: h % 3 === 0 ? HOUR_LABELS[h] : "",
      fullLabel: `${HOUR_LABELS[h]} today`,
    }));
  }
  if (filter === "week") {
    const buckets = [];
    for (let i = 6; i >= 0; i--) {
      const d = startOfDay(now);
      d.setDate(d.getDate() - i);
      buckets.push({
        key: d.toISOString().slice(0, 10),
        label: WEEKDAY_LABELS[d.getDay()],
        fullLabel: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      });
    }
    return buckets;
  }
  if (filter === "month") {
    const year = now.getFullYear();
    const month = now.getMonth();
    const days = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: days }, (_, i) => ({
      key: `d${i + 1}`,
      label: (i + 1) % 5 === 0 || i === 0 ? String(i + 1) : "",
      fullLabel: `${MONTH_LABELS[month]} ${i + 1}`,
    }));
  }
  // year
  return Array.from({ length: 12 }, (_, m) => ({
    key: `m${m}`,
    label: MONTH_LABELS[m],
    fullLabel: `${MONTH_LABELS[m]} ${now.getFullYear()}`,
  }));
}

function bucketKeyForDate(filter, date) {
  if (filter === "today") return `h${date.getHours()}`;
  if (filter === "week") return startOfDay(date).toISOString().slice(0, 10);
  if (filter === "month") return `d${date.getDate()}`;
  return `m${date.getMonth()}`;
}

function inRange(filter, date, now) {
  if (filter === "today") {
    return startOfDay(date).getTime() === startOfDay(now).getTime();
  }
  if (filter === "week") {
    const start = startOfDay(now);
    start.setDate(start.getDate() - 6);
    return date >= start && date <= now;
  }
  if (filter === "month") {
    return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
  }
  return date.getFullYear() === now.getFullYear();
}

/* ------------------------------------------------------------------ */
/*  Chart.js dashed average-line plugin (local, per-chart)            */
/* ------------------------------------------------------------------ */
function makeAvgLinePlugin(value) {
  return {
    id: "avgLine",
    afterDatasetsDraw(chart) {
      if (!value) return;
      const { ctx, chartArea, scales } = chart;
      const y = scales.y.getPixelForValue(value);
      ctx.save();
      ctx.strokeStyle = GOLD;
      ctx.setLineDash([4, 4]);
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(chartArea.left, y);
      ctx.lineTo(chartArea.right, y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = GOLD;
      ctx.font = `10px ${BODY_FONT}`;
      ctx.textAlign = "right";
      ctx.fillText(`avg ${value.toFixed(1)}`, chartArea.right - 2, y - 4);
      ctx.restore();
    },
  };
}

/* ------------------------------------------------------------------ */
/*  Chart.js bar canvas                                               */
/* ------------------------------------------------------------------ */
function BandBarChart({ chartData, overallAvg, filter, mounted }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    const labels = chartData.map((d) => d.label);
    const values = chartData.map((d) => d.displayValue);
    const colors = chartData.map((d) => {
      const base = bandColor(d.avg);
      return d.avg == null && d.pending > 0 ? hexToRgba(base, 0.55) : base;
    });

    chartRef.current = new ChartJS.Chart(canvasRef.current.getContext("2d"), {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: colors,
            borderRadius: 5,
            borderSkipped: false,
            maxBarThickness: filter === "today" || filter === "month" ? 14 : 34,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: mounted ? { duration: 650, easing: "easeOutQuart" } : false,
        scales: {
          x: {
            grid: { display: false, drawBorder: false },
            ticks: { color: INK, font: { size: 11, family: BODY_FONT }, autoSkip: false, maxRotation: 0 },
          },
          y: {
            min: 0,
            max: 9,
            ticks: { stepSize: 3, color: INK, font: { size: 11, family: BODY_FONT } },
            grid: { color: HAIRLINE, drawBorder: false },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: INK,
            titleColor: PAPER,
            titleFont: { family: BODY_FONT, size: 12 },
            bodyColor: PAPER,
            bodyFont: { family: BODY_FONT, size: 12 },
            padding: 10,
            cornerRadius: 10,
            displayColors: false,
            callbacks: {
              title: (items) => chartData[items[0].dataIndex].fullLabel,
              label: (item) => {
                const d = chartData[item.dataIndex];
                const lines = [];
                if (d.avg != null) lines.push(`Avg Band ${d.avg.toFixed(1)}`);
                else if (d.pending > 0) lines.push("Pending grading");
                else lines.push("No attempts");
                lines.push(
                  `${d.attempts} attempt${d.attempts === 1 ? "" : "s"}${d.pending > 0 ? ` · ${d.pending} pending` : ""}`
                );
                return lines;
              },
            },
          },
        },
      },
      plugins: [makeAvgLinePlugin(overallAvg)],
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData, overallAvg, filter, mounted]);

  return (
    <div className="ielts-chart-wrap" style={{ position: "relative", height: 260, width: "100%" }}>
      <canvas ref={canvasRef} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Diploma-seal gauge — the signature element (pure SVG)             */
/* ------------------------------------------------------------------ */
function BandSeal({ value, total, mounted }) {
  const rawId = useId();
  const gradientId = `sealGradient-${rawId.replace(/[^a-zA-Z0-9]/g, "")}`;
  const cx = 110;
  const cy = 108;
  const r = 84;
  const arcLength = Math.PI * r;
  const progress = Math.max(0, Math.min(1, value / 9));
  const path = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;

  const ticks = Array.from({ length: 10 }, (_, i) => {
    const angleDeg = 180 - i * 20;
    const angle = (angleDeg * Math.PI) / 180;
    const outer = r + 9;
    const inner = r - 1;
    return {
      i,
      x1: cx + inner * Math.cos(angle),
      y1: cy - inner * Math.sin(angle),
      x2: cx + outer * Math.cos(angle),
      y2: cy - outer * Math.sin(angle),
      lx: cx + (outer + 12) * Math.cos(angle),
      ly: cy - (outer + 12) * Math.sin(angle),
      major: i % 3 === 0,
    };
  });

  return (
    <div style={{ position: "relative", width: "100%", maxWidth: 220, margin: "0 auto", paddingTop: "72.73%" }}>
      <svg
        viewBox="0 0 220 160"
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid meet"
        xmlns="http://www.w3.org/2000/svg"
        style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "block" }}
      >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={BAND_COLORS.low} />
          <stop offset="38%" stopColor={BAND_COLORS.mid} />
          <stop offset="66%" stopColor={BAND_COLORS.good} />
          <stop offset="100%" stopColor={BAND_COLORS.high} />
        </linearGradient>
      </defs>

      <path d={path} fill="none" stroke="#EAE7DD" strokeWidth={14} strokeLinecap="round" />

      {ticks.map((t) => (
        <g key={t.i}>
          <line
            x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
            stroke={t.major ? INK : "#B9B6AC"}
            strokeWidth={t.major ? 2 : 1}
            opacity={0.5}
          />
          {t.major && (
            <text
              x={t.lx} y={t.ly + 4}
              fontSize="10" textAnchor="middle"
              fill={INK} opacity={0.55}
              fontFamily={BODY_FONT}
            >
              {t.i}
            </text>
          )}
        </g>
      ))}

      <path
        d={path}
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeWidth={14}
        strokeLinecap="round"
        strokeDasharray={arcLength}
        strokeDashoffset={mounted ? arcLength * (1 - progress) : arcLength}
        style={{ transition: "stroke-dashoffset 1.1s cubic-bezier(.22,1,.36,1)" }}
      />

      <text x={cx} y={cy - 14} textAnchor="middle" fontFamily={DISPLAY_FONT} fontSize="40" fontWeight="600" fill={INK}>
        {value > 0 ? value.toFixed(1) : "—"}
      </text>
      <text x={cx} y={cy + 8} textAnchor="middle" fontFamily={BODY_FONT} fontSize="12" fill={INK} opacity={0.55}>
        out of 9.0 · {total} attempt{total === 1 ? "" : "s"}
      </text>
      </svg>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                    */
/* ------------------------------------------------------------------ */
function IeltsSetsPerformanceChart({ data = [], filter = "week" }) {
  const now = useMemo(() => new Date(), []);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(false);
    const t = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(t);
  }, [filter, data]);

  const safeFilter = ["today", "week", "month", "year"].includes(filter) ? filter : "week";

  const { chartData, overallAvg, totalAttempts, totalPending } = useMemo(() => {
    const buckets = buildBuckets(safeFilter, now);
    const map = new Map(buckets.map((b) => [b.key, { ...b, scores: [], attempts: 0, pending: 0 }]));

    const inRangeItems = [];
    (data || []).forEach((item) => {
      const dateStr = item.createdAt || item.gradedAt;
      if (!dateStr) return;
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return;
      if (!inRange(safeFilter, date, now)) return;

      inRangeItems.push(item);
      const bucket = map.get(bucketKeyForDate(safeFilter, date));
      if (!bucket) return;
      bucket.attempts += 1;
      if (isPending(item)) {
        bucket.pending += 1;
      } else if (typeof item.score === "number") {
        bucket.scores.push(item.score);
      }
    });

    const chartData = Array.from(map.values()).map((b) => {
      const avg = b.scores.length
        ? ceilToHalf(b.scores.reduce((a, c) => a + c, 0) / b.scores.length)
        : null;
      const displayValue = avg != null ? avg : b.pending > 0 ? 0.35 : null;
      return { ...b, avg, displayValue };
    });

    const gradedScores = inRangeItems
      .filter((i) => !isPending(i) && typeof i.score === "number")
      .map((i) => i.score);
    const overallAvg = gradedScores.length
      ? ceilToHalf(gradedScores.reduce((a, c) => a + c, 0) / gradedScores.length)
      : 0;
    const totalPending = inRangeItems.filter(isPending).length;

    return { chartData, overallAvg, totalAttempts: inRangeItems.length, totalPending };
  }, [data, safeFilter, now]);

  return (
    <div
      className="ielts-card"
      style={{
        background: PAPER,
        color: INK,
        fontFamily: BODY_FONT,
        borderRadius: 20,
        padding: "clamp(14px, 4vw, 28px)",
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
      }}
    >
      <div
        className="ielts-inner"
        style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "clamp(14px, 3vw, 22px)", width: "100%" }}
      >
        {/* Chart card */}
        <div
          className="ielts-chart-card"
          style={{
            background: CARD,
            borderRadius: 16,
            border: `1px solid ${HAIRLINE}`,
            padding: "clamp(12px, 3vw, 20px) clamp(10px, 3vw, 16px) 8px",
            minWidth: 0,
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          {totalAttempts === 0 ? (
            <div style={{ height: 220, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", opacity: 0.5, gap: 8 }}>
              <MdOutlineAssignment size={28} strokeWidth={1.5} />
              <div style={{ fontSize: 13, textAlign: "center", padding: "0 12px" }}>No attempts recorded for this period</div>
            </div>
          ) : (
            <BandBarChart chartData={chartData} overallAvg={overallAvg} filter={safeFilter} mounted={mounted} />
          )}

          {/* Legend */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "clamp(8px, 2vw, 14px)",
              padding: "10px 4px 4px",
              borderTop: `1px solid ${HAIRLINE}`,
              marginTop: 6,
            }}
          >
            {[
              ["Below 4 · needs work", BAND_COLORS.low],
              ["4–5.5 · developing", BAND_COLORS.mid],
              ["6–6.5 · competent", BAND_COLORS.good],
              ["7+ · proficient", BAND_COLORS.high],
              ["Pending grading", BAND_COLORS.empty],
            ].map(([label, color]) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, opacity: 0.7, whiteSpace: "nowrap" }}>
                <span style={{ width: 8, height: 8, borderRadius: 999, background: color, display: "inline-block", flexShrink: 0 }} />
                {label}
              </div>
            ))}
          </div>
        </div>

        {/* Right rail */}
        <div className="ielts-rail" style={{ display: "flex", flexDirection: "column", gap: "clamp(12px, 3vw, 16px)", minWidth: 0 }}>
          <div
            style={{
              background: CARD,
              borderRadius: 16,
              border: `1px solid ${HAIRLINE}`,
              padding: "clamp(16px, 4vw, 22px) clamp(10px, 3vw, 14px)",
              textAlign: "center",
              minWidth: 0,
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <div style={{ maxWidth: 220, margin: "0 auto" }}>
              <BandSeal value={overallAvg} total={totalAttempts} mounted={mounted} />
            </div>
            <div
              style={{
                fontSize: "clamp(10.5px, 2.6vw, 12px)",
                fontWeight: 600,
                letterSpacing: "0.03em",
                textTransform: "uppercase",
                color: INK,
                opacity: 0.6,
                marginTop: 14,
                padding: "0 6px",
                wordBreak: "break-word",
              }}
            >
              Overall Average Band
            </div>
          </div>

          <div
            style={{
              background: INK,
              color: PAPER,
              borderRadius: 16,
              padding: "clamp(14px, 3vw, 18px)",
              display: "flex",
              alignItems: "center",
              gap: "clamp(10px, 2vw, 14px)",
              minWidth: 0,
              boxSizing: "border-box",
            }}
          >
            <div style={{ background: "rgba(247,246,242,0.12)", borderRadius: 12, padding: 10, display: "flex", flexShrink: 0 }}>
              <BsClock size={20} strokeWidth={1.75} color="#E8C97A" />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: DISPLAY_FONT, fontSize: "clamp(21px, 5vw, 26px)", fontWeight: 600, lineHeight: 1 }}>{totalPending}</div>
              <div style={{ fontSize: 11, opacity: 0.7, marginTop: 3, wordBreak: "break-word" }}>
                Pending exam{totalPending === 1 ? "" : "s"} awaiting grading
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .ielts-card, .ielts-card * { box-sizing: border-box; }
        @media (max-width: 640px) {
          .ielts-inner { grid-template-columns: 1fr !important; }
          .ielts-rail { flex-direction: row !important; }
          .ielts-rail > div { flex: 1 1 0 !important; min-width: 0 !important; }
        }
        @media (max-width: 420px) {
          .ielts-rail { flex-direction: column !important; }
        }
        @media (max-width: 480px) {
          .ielts-chart-wrap { height: 210px !important; }
        }
      `}</style>
    </div>
  );
}

export default IeltsSetsPerformanceChart